import React, { useMemo } from "react";

import { Box, Tooltip as MuiTooltip, Typography } from "@mui/material";
import { useReportGeneratorContext } from "components/Report-generator/context/ReportGenerator-context";
import { pdfCardWidthPx } from "components/Report-generator/utils/pageMetrics";
import { indexNames, MIN_BENCHMARK_BAR_WIDTH_PX } from "constants/reports";
import type { BenchmarkCardConfig, CardConfig } from "store/types";

import { CheckRow } from "./CheckRow";
import { SectionLabel } from "./SectionLabel";

const BENCHMARK_EXCLUDED_INDICES: string[] = [];

interface Props {
  config: BenchmarkCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
  widthPercent: number;
}

export const BenchmarkSettings: React.FC<Props> = ({ config, onUpdateConfig, widthPercent }) => {
  const { orientation } = useReportGeneratorContext();

  const maxBars = Math.floor(pdfCardWidthPx(widthPercent, orientation) / MIN_BENCHMARK_BAR_WIDTH_PX);
  const maxSelections = Math.max(maxBars - 1, 1);

  const selected = config.benchmarkIds ?? [];
  const atCap = selected.length >= maxSelections;

  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
    onUpdateConfig({ ...config, benchmarkIds: next });
  };

  const marketIndices = useMemo(
    () =>
      Object.entries(indexNames)
        .filter(([key]) => !BENCHMARK_EXCLUDED_INDICES.includes(key))
        .sort(([, a], [, b]) => a.localeCompare(b)),
    []
  );

  return (
    <Box>
      <SectionLabel>
        Benchmarks ({selected.length}/{maxSelections})
      </SectionLabel>

      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: "#9CA3AF",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mt: 1.5,
          mb: 0.5
        }}
      >
        Market (PME)
      </Typography>
      {marketIndices.map(([key, name]) => {
        const isChecked = selected.includes(key);
        const isDisabled = atCap && !isChecked;
        return (
          <MuiTooltip
            key={key}
            title={isDisabled ? `Maximum ${maxSelections} benchmarks for this card width` : ""}
            placement="left"
            arrow
          >
            <span>
              <CheckRow label={name} checked={isChecked} onChange={() => toggle(key)} disabled={isDisabled} />
            </span>
          </MuiTooltip>
        );
      })}
    </Box>
  );
};
